import React, { useState, useEffect, useRef } from "react";
import {
  Container,
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Mic,
  MicOff,
  Videocam,
  VideocamOff,
  Stop,
  PlayArrow,
  SmartToy,
  CheckCircle,
  RadioButtonChecked,
} from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import API_BASE_URL from "../Config/config";

function TechnicalInterview() {
  const { applicationId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [context, setContext] = useState(null);
  const [stage, setStage] = useState("instructions"); // instructions, media, questioning, finished
  const isSubmittingRef = useRef(false);

  const [isCameraOn, setIsCameraOn] = useState(false);
  const [isMicOn, setIsMicOn] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const timerRef = useRef(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (applicationId && localStorage.getItem(`terminated_${applicationId}`)) {
      localStorage.removeItem(`terminated_${applicationId}`);
      setError("terminated");
      setStage("finished");
      // Optionally notify backend if not already done
      const url = `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${applicationId}`;
      const payload = JSON.stringify({ applicationId, interviewType: 'TECHNICAL_EVALUATION', isTerminated: true, answers: [] });
      if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    }
    fetchContext();

    const handleUnload = () => {
      if (isSubmittingRef.current || stage === "finished") return;

      // 🔥 Force termination on refresh/close
      if (stage !== "finished" && stage !== "instructions") {
        localStorage.setItem(`terminated_${applicationId}`, 'true');
      }

      const url = `${API_BASE_URL}/interviewRoutes/ai-interview/terminate/${applicationId}`;
      const payload = JSON.stringify({
        applicationId,
        interviewType: 'TECHNICAL_EVALUATION',
        isTerminated: true,
        answers: questions.map((q, idx) => ({
          question: q,
          answer: transcript,
        })),
      });

      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, payload);
      } else {
        fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: payload,
          keepalive: true,
        });
      }
    };

    window.addEventListener("beforeunload", handleUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      stopStream();
      if (recognitionRef.current) recognitionRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener("beforeunload", handleUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [applicationId, questions, transcript, stage]);

  useEffect(() => {
    if (timeLeft === 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      handleTimeExpired();
    }
  }, [timeLeft]);

  const handleTimeExpired = () => {
    setStage("finished");
    // Optionally alert user or just submit
    alert("Time is up! Your interview is being submitted.");
    handleSubmit();
  };

  const startTimer = (minutes) => {
    const seconds = (minutes || 30) * 60;
    setTimeLeft(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  };

  const formatTime = (seconds) => {
    if (seconds === null) return "--:--";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const fetchContext = async () => {
    try {
      console.log("Fetching interview context for application:", applicationId);
      // Reusing the same context endpoint as AI interview for resume/job details
      const response = await axios.get(
        `/api/ai-interview/context/${applicationId}`,
      );
      console.log("Context received:", response.data);
      setContext(response.data);

      // If the backend provides questions for technical interview specifically, we'd use those.
      // For now, we'll use generated questions or fallbacks.
      if (
        response.data.generatedQuestions &&
        Array.isArray(response.data.generatedQuestions) &&
        response.data.generatedQuestions.length > 0
      ) {
        console.log(
          "Using generated questions:",
          response.data.generatedQuestions,
        );
        setQuestions(response.data.generatedQuestions);
        setLoading(false);
        return;
      } else {
        console.warn(
          "No generated questions found in context response, checking for technical interview questions from database",
        );

        // Secondary attempt: specifically look for technical interview questions if AI failed
        try {
          const techInterviewRes = await axios.get(
            `/api/technical-interview/${applicationId}`,
          );
          if (techInterviewRes.data && techInterviewRes.data.questions) {
            let questions = techInterviewRes.data.questions;
            if (typeof questions === "string") {
              try {
                questions = JSON.parse(questions);
              } catch (e) {}
            }
            if (Array.isArray(questions) && questions.length > 0) {
              console.log(
                "Using questions from technical_interviews table:",
                questions,
              );
              setQuestions(questions);
              setLoading(false);
              return;
            }
          }
        } catch (techErr) {
          console.error(
            "Failed to fetch technical interview specifically:",
            techErr,
          );
        }

        console.warn(
          "All question sources exhausted, using hardcoded fallbacks",
        );
        setQuestions([
          "Can you start by introducing yourself and your technical background?",
          "What is the most challenging technical project you've worked on recently?",
          "How do you handle debugging complex issues in a production environment?",
          "What technical skills or technologies are you currently most proficient in?",
          "Do you have any questions for us regarding the technical team or role?",
        ]);
      }
    } catch (err) {
      console.error("Failed to fetch context:", err);
      if (err.response?.status === 400 && err.response?.data?.message === "Interview already terminated") {
        setError("terminated");
      } else {
        setError("Failed to initialize technical interview. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const startMediaCheck = async () => {
    setStage("media");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setIsCameraOn(true);
      setIsMicOn(true);
    } catch (err) {
      console.error("Media access error:", err);
      setError(
        "Camera and Microphone access are required. Please check your browser permissions.",
      );
    }
  };

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    setIsCameraOn(false);
    setIsMicOn(false);
  };

  const startInterview = () => {
    setStage("questioning");
    startTimer(context?.timeLimit || 30);
  };

  const toggleRecording = () => {
    if (!isRecording) {
      startListening();
    } else {
      stopListening();
    }
  };

  const startListening = () => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) {
      setError("Your browser does not support Speech Recognition. Please use Chrome.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;

    recognition.onstart = () => {
      setIsRecording(true);
    };

    recognition.onresult = (event) => {
      let text = "";

      for (let i = 0; i < event.results.length; i++) {
        text += event.results[i][0].transcript;
      }

      setTranscript(text);
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();

    // stop after 30 seconds
    setTimeout(() => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
        handleNextQuestion();
      }
    }, 30000);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setIsRecording(false);
  };

  const handleNextQuestion = () => {
    stopListening();

    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
    } else {
      // For the last question, we just move to finished
      setStage("finished");
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const handleSubmit = async () => {
    try {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setLoading(true);

      // Stop recognition if still running
      if (isRecording) stopListening();

      // Submit the interview data
      await axios.post(`/api/technical-interview/${applicationId}/submit`, {
        answers: questions.map((q, idx) => ({
          question: q,
          answer: transcript, // Using the full transcript for now as it captures all recorded speech
        })),
        score: 0,
      });
      navigate("/candidate/dashboard");
    } catch (err) {
      console.error("Submission error:", err);
      setError("Failed to submit interview responses.");
    } finally {
      setLoading(false);
    }
  };

  if (error === "terminated") {
    return (
      <Box sx={{ textAlign: "center", mt: 10 }}>
        <Typography variant="h4" gutterBottom>Interview Already Terminated</Typography>
        <Button variant="contained" onClick={() => navigate("/candidate/dashboard")}>
          Go Back
        </Button>
      </Box>
    );
  }

  if (loading && !context) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4, maxWidth: "md" }}>
      <Paper sx={{ p: 4, borderRadius: 4, boxShadow: 6 }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ fontWeight: "bold", color: "#1E3A8A" }}
        >
          Technical Interview Panel
        </Typography>
        <Divider sx={{ mb: 4 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {stage === "instructions" && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Instructions
            </Typography>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Ensure you are in a quiet environment with adequate lighting for clear recording." />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Make sure your camera and microphone are functioning properly, as they will capture your responses." />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Read each question carefully and provide thoughtful, detailed answers." />
              </ListItem>

              <ListItem>
                <ListItemIcon>
                  <CheckCircle color="success" />
                </ListItemIcon>
                <ListItemText primary="Click 'Start Recording' to begin and 'Stop Recording' when you have completed your response." />
              </ListItem>
            </List>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={startMediaCheck}
              sx={{ mt: 4, py: 1.5, borderRadius: 3, bgcolor: "#1E3A8A" }}
            >
              Proceed to Media Check
            </Button>
          </Box>
        )}

        {stage === "media" && (
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" gutterBottom>
              Camera & Microphone Check
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: 400,
                height: 300,
                bgcolor: "#000",
                mx: "auto",
                borderRadius: 2,
                overflow: "hidden",
                position: "relative",
                mb: 3,
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  transform: "scaleX(-1)",
                }}
              />
              {!isCameraOn && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                  }}
                >
                  <VideocamOff sx={{ fontSize: 64, opacity: 0.5 }} />
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              If you can see yourself and the mic levels are moving (not shown,
              but requested), you are ready.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={startInterview}
              disabled={!isCameraOn}
              sx={{ py: 1.5, px: 6, borderRadius: 3, bgcolor: "#1E3A8A" }}
            >
              Start Interview
            </Button>
          </Box>
        )}

        {stage === "questioning" && (
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#000",
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  style={{
                    width: "100%",
                    display: "block",
                    transform: "scaleX(-1)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    top: 10,
                    left: 10,
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: "#fff",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                    Time Remaining: {formatTime(timeLeft)}
                  </Typography>
                </Box>
                {isRecording && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      bgcolor: "error.main",
                      color: "#fff",
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <RadioButtonChecked
                      sx={{ fontSize: 16, animation: "pulse 1s infinite" }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: "bold" }}>
                      REC
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                <Button
                  variant="contained"
                  color={isRecording ? "error" : "primary"}
                  startIcon={isRecording ? <Stop /> : <Mic />}
                  onClick={toggleRecording}
                  sx={{ borderRadius: 3 }}
                >
                  {isRecording ? "Stop Recording" : "Start Recording"}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  bgcolor: "#f9f9f9",
                }}
              >
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </Typography>
                <Typography variant="h6" sx={{ mb: 3 }}>
                  {(() => {
                    const q = questions[currentQuestionIndex];
                    if (!q) return "";
                    if (typeof q === "string") {
                      try { const p = JSON.parse(q); return p?.text || p?.question || q; } catch { return q; }
                    }
                    return q?.text || q?.question || (typeof q === "object" ? "" : q);
                  })()}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Transcript preview:
                </Typography>
                <Box
                  sx={{
                    flexGrow: 1,
                    p: 2,
                    bgcolor: "#fff",
                    border: "1px solid #ddd",
                    borderRadius: 1,
                    overflowY: "auto",
                    maxHeight: 200,
                  }}
                >
                  <Typography variant="body2">
                    {transcript ||
                      (isRecording ? "Listening..." : "Recording not started.")}
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleNextQuestion}
                  sx={{ mt: 3, py: 1 }}
                >
                  {currentQuestionIndex < questions.length - 1
                    ? "Next Question"
                    : "Finish Interview"}
                </Button>
              </Paper>
            </Grid>
          </Grid>
        )}

        {stage === "finished" && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CheckCircle color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Interview Completed!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your responses have been recorded and will be submitted for
              review.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ px: 8, py: 1.5, borderRadius: 3, bgcolor: "#1E3A8A" }}
            >
              {loading ? <CircularProgress size={24} /> : "Submit Interview"}
            </Button>
          </Box>
        )}
      </Paper>

      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
    </Container>
  );
}

export default TechnicalInterview;
